<template>
  <div :class="$style['games-gallery']">
    <base-swiper
      ref="swiper"
      :data="landingGames"
      fade
      :autoplay="{ delay: 3000, disableOnInteraction: true }"
      :slide-change="handleSlideChange"
    >
      <template #default="{ el }">
        <div :class="$style['game-image']">
          <img :src="isPortraitMode ? el.image_portrait : el.image" />
        </div>
        <div :class="$style['game-info']">
          <h3 :class="$style['game-title']">{{ el.title }}</h3>
          <p :class="$style['game-subtitle']">{{ el.subtitle }}</p>
          <p :class="$style['game-summary']">{{ el.summary }}</p>
          <div :class="$style['game-description']" v-html="el.description" />
          <div :class="$style['game-links']">
            <span
              v-for="(link, index) in el.links"
              :key="index"
              :class="[
                $style['game-links-item'],
                $style['game-links-item-' + link.id],
              ]"
            >
              <template v-if="link.type === 'qrc'">
                {{ link.name }}
                <span :class="$style.qrcodeTip">
                  <img :src="link.url" />
                </span>
              </template>
              <a v-else :href="link.url" target="_blank">
                {{ link.name }}
              </a>
            </span>
          </div>
        </div>
      </template>
    </base-swiper>

    <!-- 游戏分页导航 -->
    <nav :class="$style['game-pagination']">
      <!-- 向左导航按钮 -->
      <button
        :class="$style['btn-nav-prev']"
        :disabled="landingGames.length <= 1"
        @click="handlePrevGame"
      />

      <!-- 游戏缩略图分页导航 -->
      <button
        v-for="(game, index) in landingGames"
        :key="index"
        :class="[
          $style['game-pagination-item'],
          {
            [$style['game-pagination-item-active']]:
              index === currentSlideIndex,
          },
        ]"
        @click="handlePaginationClick(index)"
      >
        <img :src="game.thumbnail" />
      </button>

      <!-- 向右导航按钮 -->
      <button
        :class="$style['btn-nav-next']"
        :disabled="landingGames.length <= 1"
        @click="handleNextGame"
      />
    </nav>
    <scroll-indicator :class="$style['scroll-indicator']" />
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import BaseSwiper from '../BaseSwiper'
import ScrollIndicator from '../ScrollIndicator'

export default {
  name: 'LandingGames',
  components: {
    BaseSwiper,
    ScrollIndicator,
  },
  data() {
    return {
      currentSlideIndex: 0,
      // 当前所在屏幕索引（从1开始）
      currentSection: 1,
      // fullpage.js 实例引用
      fullpageInstance: null,
      // 是否为竖屏模式
      isPortraitMode: false,
    }
  },
  computed: {
    ...mapGetters(['landingGames']),
  },
  watch: {
    // 监听 currentSection 变化
    currentSection(newVal, oldVal) {
      // 延迟执行，确保 DOM 更新完成
      setTimeout(() => {
        const swiperInstance = this.$refs.swiper && this.$refs.swiper.mySwiper
        if (swiperInstance && swiperInstance.autoplay) {
          if (newVal === 2) {
            // 进入当前页，启动轮播
            swiperInstance.autoplay.start()
          } else {
            // 离开当前页，停止轮播
            swiperInstance.autoplay.stop()
          }
        }
      }, 100)
    },
  },
  mounted() {
    this.updateCurrentSection()

    // 初始时如果不在当前页则停止轮播
    if (this.currentSection !== 2 && this.$refs.swiper) {
      const swiperInstance = this.$refs.swiper.mySwiper
      if (swiperInstance && swiperInstance.autoplay) {
        swiperInstance.autoplay.stop()
      }
    }

    // 添加全局事件监听器
    this.$root.$on('screen-info-changed', this.handleScreenInfoChange)
  },
  beforeDestroy() {
    // 移除全局事件监听器
    this.$root.$off('screen-info-changed', this.handleScreenInfoChange)
  },
  methods: {
    // 更新当前所在屏幕索引
    updateCurrentSection(sectionNumber = null) {
      if (sectionNumber !== null) {
        // 直接设置指定的屏幕索引
        this.currentSection = sectionNumber
      } else if (this.fullpageInstance) {
        // 从 fullpage.js 实例获取当前激活的屏幕索引
        const activeSection = this.fullpageInstance.getActiveSection()
        if (activeSection) {
          this.currentSection = activeSection.index + 1
        }
      }
    },
    // 设置 fullpage.js 实例引用（由父组件调用）
    setFullpageInstance(instance) {
      this.fullpageInstance = instance
    },
    // 处理屏幕方向变化的事件监听器
    handleScreenInfoChange(newScreenInfo) {
      if (!newScreenInfo) {
        return
      }
      this.isPortraitMode = newScreenInfo.type !== 'landscape'
    },
    handlePaginationClick(index) {
      if (index === this.currentSlideIndex) return
      // 切换到对应的轮播图
      if (this.$refs.swiper) {
        this.$refs.swiper.slideTo(index)
      }
    },
    handleSlideChange(swiper) {
      this.currentSlideIndex = swiper.realIndex
    },
    // 处理向左导航按钮点击
    handlePrevGame() {
      if (this.landingGames.length <= 1) return

      let prevIndex = this.currentSlideIndex - 1
      if (prevIndex < 0) {
        // 如果当前是第一个游戏，跳到最后一个
        prevIndex = this.landingGames.length - 1
      }

      this.handlePaginationClick(prevIndex)
    },
    // 处理向右导航按钮点击
    handleNextGame() {
      if (this.landingGames.length <= 1) return

      let nextIndex = this.currentSlideIndex + 1
      if (nextIndex >= this.landingGames.length) {
        // 如果当前是最后一个游戏，跳到第一个
        nextIndex = 0
      }

      this.handlePaginationClick(nextIndex)
    },
  },
}
</script>

<style lang="less" module>
.games-gallery {
  .size(100%);

  position: relative;
  background: url(./images/bg.jpg) repeat;
  background-size: contain;
  color: @landing-text-color;

  :global(.swiper-container),
  :global(swiper-slide) {
    .size(100%);
  }
}

.game-info {
  position: absolute;
  left: 0;
  background-size: 100% 100%;
}

.game-image {
  position: absolute;
  top: 0;
  right: 0;
  background-size: 100% 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
  }
}

.game-title {
  font-weight: 700;
  opacity: 0;
}

.game-subtitle {
  position: relative;
  margin: 0.1rem 2rem 0 0;
  padding-bottom: 0.12rem;
  color: #afafaf;
  border-bottom: 0.01rem solid #afafaf;
  opacity: 0;

  &::after {
    .size(1.92rem, 0.05rem);

    content: '';
    position: absolute;
    bottom: -0.03rem;
    left: 0;
    background-color: #ff1325;
  }
}

.game-summary,
.game-description {
  margin-top: 0.2rem;
  line-height: 1.8;
  opacity: 0;
}

.game-links {
  display: flex;
  gap: 0.4rem;
  opacity: 0;

  &-item {
    position: relative;
    font-size: 0.28rem;
    font-weight: 700;
    cursor: pointer;

    a {
      color: inherit;
      text-decoration: none;
    }

    // 二维码
    > span {
      .size(1.72rem, 1.93rem);

      position: absolute;
      left: 50%;
      transform: translateX(-50%) translateY(10px);
      background: url('./images/qrc-bg.png') no-repeat;
      background-size: 100% 100%;
      text-align: center;
      padding-top: 0.32rem;
      cursor: default;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
      pointer-events: none;

      img {
        .size(1.47rem);
      }
    }

    &:hover {
      color: #ff1325;

      > span {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
        visibility: visible;
        pointer-events: auto;
      }
    }

    // 当鼠标在二维码区域或连接区域时保持显示
    > span:hover,
    > span:hover::before {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
      visibility: visible;
      pointer-events: auto;
    }
  }
}

/* 游戏分页导航样式 */
.game-pagination {
  z-index: 9;
  position: absolute;
  display: flex;
  align-items: center;

  &-item {
    .size(1.18rem);

    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.28rem;

    img {
      .size(1.16rem);

      pointer-events: none;
    }

    &-active::after {
      .size(100%);

      content: '';
      position: absolute;
      top: 0;
      left: 0;
      background: url('./images/pagination-selected.png') no-repeat;
      background-size: 100% 100%;
      pointer-events: none;
    }
  }
}

// 导航按钮基础样式
.btn-nav-prev,
.btn-nav-next {
  .size(0.6rem);

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: breathe 1.5s ease-in-out infinite;

  &::after {
    content: '';
    background-repeat: no-repeat;
    background-size: contain;
    pointer-events: none;
    transition: transform 0.3s ease;
  }

  &:hover:not(:disabled) {
    &::after {
      transform: scale(1.1);
    }
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    animation: none;
  }
}

.btn-nav-prev {
  &::after {
    transform: rotate(180deg);
  }

  &:hover:not(:disabled) {
    &::after {
      transform: rotate(180deg) scale(1.1);
    }
  }
}

// 呼吸感动画
@keyframes breathe {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.15);
  }
}

// fullpage.js 完全进入页面时的动画效果
:global(.fp-completely .swiper-slide-active) {
  .game-title,
  .game-subtitle,
  .game-summary,
  .game-description,
  .game-links {
    animation: fadeInRight 1s ease forwards;
  }

  .game-subtitle {
    animation-delay: 0.2s;
  }

  .game-summary {
    animation-delay: 0.4s;
  }

  .game-description {
    animation-delay: 0.6s;
  }

  .game-links {
    animation-delay: 0.8s;
  }

  .game-image img {
    animation: fadeInLeft 2s ease forwards;
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(-0.5rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translateX(0.5rem);
  }
  50% {
    opacity: 1;
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

:global(.fp-completely) {
  .scroll-indicator {
    animation: fadeIn 1s ease forwards;
    animation-delay: 1s;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// 横屏
:global(.landing-screen-landscape) {
  .game-info {
    .size(13.52rem, 10.8rem);

    top: 0;
    background-image: url(./images/landscape-info-bg.png);
    padding: 3.1rem 6.4rem 0 0.8rem;
  }

  .game-title {
    font-size: 0.66rem;
  }

  .game-subtitle,
  .game-summary,
  .game-description {
    font-size: 0.24rem;
  }

  .game-links {
    margin-top: 0.3rem;

    &-item {
      // 二维码
      > span {
        bottom: -2rem;
      }
    }
  }

  .game-image {
    .size(13.55rem, 8.94rem);

    background-image: url(./images/landscape-image-bg.png);
  }

  .game-pagination {
    bottom: 1rem;
    right: 0.8rem;
  }

  .btn-nav-prev,
  .btn-nav-next {
    &::after {
      .size(0.41rem, 0.42rem);

      background-image: url('./images/landscape-btn-arrow.png');
    }
  }

  .btn-nav-prev {
    margin-right: 0.28rem;
  }

  .scroll-indicator {
    left: 0.92rem;
    bottom: 0.37rem;
    opacity: 0;
  }
}

// 竖屏
:global(.landing-screen-portrait) {
  .game-info {
    .size(10.8rem, 13.41rem);

    top: 7.5rem;
    background-image: url(./images/portrait-info-bg.png);
    padding: 4.68rem 0.57rem 0;
  }

  .game-title {
    font-size: 0.68rem;
  }

  .game-subtitle {
    margin-right: 5.43rem;
    font-size: 0.26rem;
  }

  .game-summary,
  .game-description {
    font-size: 0.32rem;
  }

  .game-links {
    margin-top: 0.5rem;
    justify-content: space-around;

    &-item {
      .size(1.12rem);

      background-size: 100% 100%;
      font-size: 0;
      color: transparent;
      line-height: 0;

      a {
        .size(100%);

        display: block;
      }

      &-website {
        background-image: url('./images/icon-website.png');
      }

      &-wechat {
        background-image: url('./images/icon-wechat.png');
      }

      &-taptap {
        background-image: url('./images/icon-taptap.png');
      }

      &-bilibili {
        background-image: url('./images/icon-bilibili.png');
      }

      // 二维码
      > span {
        top: -2rem;
        transform: translateX(-50%) translateY(0) rotate(180deg);

        img {
          transform: rotate(-180deg);
        }
      }
    }
  }

  .game-image {
    .size(10.8rem, 11.93rem);

    background-image: url(./images/portrait-image-bg.png);

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0.62rem;
      height: 1.5rem;
      background-color: fade(#2a2a2a, 45%);
    }
  }

  .game-pagination {
    top: 10.42rem;
    right: 0.62rem;
    height: 1.5rem;

    &-item:nth-child(4) {
      margin-right: 0.18rem;
    }
  }

  .btn-nav-prev,
  .btn-nav-next {
    &::after {
      .size(0.47rem, 0.39rem);

      background-image: url('./images/portrait-btn-arrow.png');
    }
  }

  .btn-nav-prev {
    margin-right: 0.18rem;
  }

  .scroll-indicator {
    left: 0;
    right: 0;
    bottom: 0.3rem;
    margin: 0 auto;
    opacity: 0;
  }
}
</style>
